import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export const cn = (...inputs: ClassValue[]) => twMerge(clsx(inputs));

// ── Formatting ──────────────────────────────────────────────────────────

const inr = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  maximumFractionDigits: 0,
});

const inrPrecise = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  minimumFractionDigits: 2,
});

export const formatMoney = (value: unknown, precise = false): string => {
  const n = Number(value ?? 0);
  if (!Number.isFinite(n)) return "₹0";
  return precise ? inrPrecise.format(n) : inr.format(n);
};

const compact = new Intl.NumberFormat("en-IN", {
  notation: "compact",
  maximumFractionDigits: 1,
});

export const formatNumber = (value: unknown, useCompact = false): string => {
  const n = Number(value ?? 0);
  if (!Number.isFinite(n)) return "0";
  return useCompact && n >= 10_000
    ? compact.format(n)
    : new Intl.NumberFormat("en-IN").format(n);
};

export const formatDate = (value: string | Date | null | undefined): string => {
  if (!value) return "—";
  const d = typeof value === "string" ? new Date(value) : value;
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

export const formatDateTime = (
  value: string | Date | null | undefined,
): string => {
  if (!value) return "—";
  const d = typeof value === "string" ? new Date(value) : value;
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const relativeTime = (
  value: string | Date | null | undefined,
): string => {
  if (!value) return "—";
  const d = typeof value === "string" ? new Date(value) : value;
  if (Number.isNaN(d.getTime())) return "—";

  const seconds = Math.floor((Date.now() - d.getTime()) / 1000);
  if (seconds < 45) return "just now";

  const units: [number, Intl.RelativeTimeFormatUnit][] = [
    [60, "second"],
    [3600, "minute"],
    [86_400, "hour"],
    [604_800, "day"],
    [2_629_800, "week"],
    [31_557_600, "month"],
    [Infinity, "year"],
  ];

  const rtf = new Intl.RelativeTimeFormat("en", { numeric: "auto" });
  let divisor = 1;
  for (const [limit, unit] of units) {
    if (Math.abs(seconds) < limit) {
      return rtf.format(-Math.round(seconds / divisor), unit);
    }
    divisor = limit;
  }
  return formatDate(d);
};

/** Minutes → "2h 15m". Used for SLA and response-time displays. */
export const formatDuration = (minutes: number | null | undefined): string => {
  if (minutes == null || !Number.isFinite(minutes)) return "—";
  const m = Math.round(Math.abs(minutes));
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  if (h < 24) return rem > 0 ? `${h}h ${rem}m` : `${h}h`;
  const d = Math.floor(h / 24);
  const remH = h % 24;
  return remH > 0 ? `${d}d ${remH}h` : `${d}d`;
};

export const initials = (name: string | null | undefined): string => {
  if (!name?.trim()) return "?";
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? "")
    .join("");
};

export const truncate = (value: string | null | undefined, max = 60): string => {
  if (!value) return "—";
  return value.length > max ? `${value.slice(0, max - 1)}…` : value;
};

/** Mask a phone number for display in lists. */
export const maskPhone = (phone: string | null | undefined): string => {
  if (!phone) return "—";
  const digits = phone.replace(/\D/g, "");
  if (digits.length < 6) return phone;
  return `${digits.slice(0, 2)}••••${digits.slice(-4)}`;
};

export const debounce = <A extends unknown[]>(
  fn: (...args: A) => void,
  ms = 300,
) => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  return (...args: A) => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => fn(...args), ms);
  };
};

export const titleCase = (value: string): string =>
  value
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
